import React, { useRef, useCallback, useState, useEffect } from "react";
import classNames from "classnames";
import { Icon } from "~/components";
import { getAnalyticsClickTrackingAttributes } from "~/utils/adobeDataLayerUtils";
import styles from "./Carousel.module.css";

export const Carousel = ({
  children,
  className,
  title,
  showNavigation = true,
  gap = 24,
  centerMode = false,
  onTitleClick,
  titleHref,
  navButtonTop,
  ...props
}) => {
  const carouselRef = useRef(null);
  const [showPrevious, setShowPrevious] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const childrenCount = React.Children.count(children);
  const shouldCenter = centerMode || (childrenCount > 0 && childrenCount <= 3);

  const checkScrollButtons = useCallback(() => {
    if (!carouselRef.current || !showNavigation) return;

    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;

    // If content fits within the container, hide all buttons
    if (scrollWidth <= clientWidth) {
      setShowPrevious(false);
      setShowNext(false);
      return;
    }

    setShowPrevious(scrollLeft > 10); // Decreased threshold for better responsiveness
    // Use a small buffer (5px) for the end detection to account for potential rounding errors
    setShowNext(scrollLeft < scrollWidth - clientWidth - 5);
  }, [showNavigation]);

  useEffect(() => {
    // Check buttons immediately and on any changes
    checkScrollButtons();

    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener("scroll", checkScrollButtons);
      window.addEventListener("resize", checkScrollButtons);

      // Also use ResizeObserver to handle container size changes or content changes that affect scrollWidth
      // This is important when switching from skeleton (many items) to actual content (few items)
      const resizeObserver = new ResizeObserver(() => {
        checkScrollButtons();
      });
      resizeObserver.observe(carousel);
      // Observe children changes if possible, but observing the container size/content rect is usually enough
      // for most layout shifts. However, scrollWidth changes don't always trigger ResizeObserver on the element itself
      // unless overflow changes.

      return () => {
        carousel.removeEventListener("scroll", checkScrollButtons);
        window.removeEventListener("resize", checkScrollButtons);
        resizeObserver.disconnect();
      };
    }
  }, [checkScrollButtons, childrenCount]); // Re-run when number of children changes

  const scrollTo = useCallback((direction) => {
    if (!carouselRef.current) return;

    const scrollAmount = 320; // Card width + gap
    const currentScroll = carouselRef.current.scrollLeft;
    const targetScroll =
      direction === "next"
        ? currentScroll + scrollAmount
        : currentScroll - scrollAmount;

    carouselRef.current.scrollTo({
      left: targetScroll,
      behavior: "smooth",
    });
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "ArrowLeft") {
        scrollTo("previous");
      } else if (event.key === "ArrowRight") {
        scrollTo("next");
      }
    },
    [scrollTo],
  );

  return (
    <div className={classNames(styles.wrapper, className)} {...props}>
      {title && (
        <div className={styles.header}>
          {titleHref ? (
            <a
              href={titleHref}
              className={classNames(styles.titleLink, "custom_click_track")}
              {...getAnalyticsClickTrackingAttributes(
                title,
                "click",
                "Carousel Title Link",
              )}
            >
              <h2 className={styles.title}>{title}</h2>
              <Icon name="arrowRightNoLine" className={styles.titleChevron} />
            </a>
          ) : onTitleClick ? (
            <button
              onClick={onTitleClick}
              className={classNames(styles.titleLink, "custom_click_track")}
              type="button"
              {...getAnalyticsClickTrackingAttributes(
                title,
                "click",
                "Carousel Title Button",
              )}
            >
              <h2 className={styles.title}>{title}</h2>
              <Icon name="arrowRightNoLine" className={styles.titleChevron} />
            </button>
          ) : (
            <>
              <h2 className={styles.title}>{title}</h2>
            </>
          )}
        </div>
      )}

      <div className={styles.container}>
        {/* Desktop navigation buttons - absolutely positioned */}
        {showNavigation && showPrevious && (
          <button
            className={classNames(
              styles.navButton,
              styles.navButtonPrevious,
              "custom_click_track",
            )}
            onClick={() => scrollTo("previous")}
            aria-label="Scroll to previous items"
            type="button"
            style={navButtonTop ? { top: navButtonTop } : undefined}
            {...getAnalyticsClickTrackingAttributes(title, "swipe", "previous")}
          >
            <Icon name="arrowRightNoLine" inverse={true} />
          </button>
        )}
        {showNavigation && showNext && (
          <button
            className={classNames(
              styles.navButton,
              styles.navButtonNext,
              "custom_click_track",
            )}
            onClick={() => scrollTo("next")}
            aria-label="Scroll to next items"
            type="button"
            style={navButtonTop ? { top: navButtonTop } : undefined}
            {...getAnalyticsClickTrackingAttributes(title, "swipe", "next")}
          >
            <Icon name="arrowRightNoLine" />
          </button>
        )}

        <div
          className={classNames(styles.carousel, {
            [styles.carouselCenter]: shouldCenter,
          })}
          ref={carouselRef}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          style={{ "--carousel-gap": `${gap}px` }}
        >
          <div className={styles.content}>
            {React.Children.map(children, (child, index) => (
              <div key={index} className={styles.item}>
                {child}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
